import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { id: "modal-container-element" }
const _hoisted_2 = { key: 1 }
const _hoisted_3 = { class: "search-container" }
const _hoisted_4 = ["value"]
const _hoisted_5 = ["rows", "server-side", "total-items", "current-page", "loading"]
const _hoisted_6 = [".open", "title"]
const _hoisted_7 = { class: "modal-content" }
const _hoisted_8 = ["value", "label"]
const _hoisted_9 = { slot: "footer" }
const _hoisted_10 = ["disabled"]
const _hoisted_11 = [".open"]
const _hoisted_12 = { class: "modal-content" }
const _hoisted_13 = ["value"]
const _hoisted_14 = { slot: "footer" }
const _hoisted_15 = ["disabled"]

import { useStore } from 'vuex';
import { ref, onMounted, onUnmounted, computed, watchEffect, watch } from 'vue';
import { EPINIO_TYPES } from '../types';
import Banner from '@components/Banner/Banner.vue';
import Masthead from '@shell/components/ResourceList/Masthead';
import { epinioExceptionToErrorsArray } from '../utils/errors';
import { validateKubernetesName } from '@shell/utils/validators/kubernetes-name';
import { startPolling, stopPolling } from '../utils/polling';
import { makeActionMenu } from '../utils/table-formatters';
import EpinioNamespace from 'models/namespaces';
import { overrideTableRows } from '../utils/table-formatters';
import { debounce } from 'lodash';


export default /*@__PURE__*/_defineComponent({
  __name: 'namespaces',
  props: {
    schema: {},
    rows: {}
  },
  setup(__props: any) {



const store = useStore() as any;
const t = store.getters['i18n/t'];

const errors = ref<Array<string>>([]);
const resource: string = EPINIO_TYPES.NAMESPACE;
const displayRows = ref<EpinioNamespace[]>([]);

const paginationMeta = computed(() => store.getters['epinio/paginationMeta'](resource));
const currentPage = computed(() => store.getters['epinio/currentPaginationPage'](resource));

const searchQuery = ref<string>('');

const paginating = ref(false);

async function goToPage(page: number) {
  const meta = paginationMeta.value;

  if (meta && (page < 1 || page > meta.totalPages)) return;
  paginating.value = true;
  try {
    await store.dispatch('epinio/goToPage', { type: resource, page });
  } finally {
    paginating.value = false;
  }
}

const onSearch = debounce(async (query: string) => {
  paginating.value = true;
  try {
    await store.dispatch('epinio/search', { type: resource, query });
  } finally {
    paginating.value = false;
  }
}, 500);

watch(searchQuery, (newQuery) => {
  onSearch(newQuery);
});

const value = ref<EpinioNamespace>({ meta: { name: '' } } as EpinioNamespace);
const showCreateModal = ref<boolean>(false);
const namespaceNameInput = ref<HTMLElement | null>(null);
const creatingNamespace = ref<boolean>(false);

const namespaceToDelete = ref<EpinioNamespace | null>(null);
const showDeleteModal = ref<boolean>(false);
const deleteNamespaceInput = ref<HTMLElement | null>(null);
const deletingNamespace = ref<boolean>(false);
const confirmDeleteInput = ref<string>('');

// Strict RBAC: only show Create/Delete when the user has namespace write perms (admin).
// Defined ahead of the watchEffect that consumes them to avoid a TDZ on first run.
const canCreateNamespace = computed(() => {
  const can = store.getters['epinio/can'];
  const perms = store.getters['epinio/permissions']?.();

  if (!can || !perms || Object.keys(perms).length === 0) {
    return false;
  }

  // Create is cluster-scoped: gate on the global-only namespace_create.
  return can('namespace_create');
});
// Per-namespace delete is namespaced (server authorizes it against the role for
// the namespace being deleted), so it stays on the flat namespace_write and is
// further gated per-row by row.canDelete below.
const canDelete = computed(() => {
  const can = store.getters['epinio/can'];
  const perms = store.getters['epinio/permissions']?.();

  if (!can || !perms || Object.keys(perms).length === 0) {
    return false;
  }

  return can('namespace_write') || can('namespace');
});

watchEffect(() => {
  const all = store.getters['epinio/all'](EPINIO_TYPES.NAMESPACE) as EpinioNamespace[];

  // Touch meta so _MERGE polling (which deletes/re-adds all properties) re-runs this effect
  all.forEach((row) => { void row.meta; });

  // Add custom namespace delete action to replace the built in rancher shell flow.
  // Gate by namespace write perms so view-only / app-only roles don't see Delete.
  const overrideProps = [{
    prop: 'availableActions',
    value: (row: any) => {
      if (!canDelete.value || !row.canDelete) {
        return [];
      }

      return [{
        action: 'removeNamespace',
        altAction: 'remove',
        bulkAction: 'removeNamespace',
        bulkable: true,
        enabled: true,
        icon: 'icon icon-trash',
        label: 'Delete',
        weight: -10
      }]
    },
    conditionFn: () => true,
  },
  {
    prop: 'removeNamespace',
    value: (row: EpinioNamespace) => () => {
      namespaceToDelete.value = row;
      openDeleteModal();
    },
    conditionFn: (row: EpinioNamespace) => canDelete.value && row.canDelete,
  }];
  displayRows.value = overrideTableRows(all, overrideProps);
});

const validateCreate = computed(() => {
  if (!value.value.meta.name?.length) {
    return false;
  }

  const validationErrors = getNamespaceErrors(value.value.meta.name);

  return validationErrors.length === 0;
});


const validateDelete = computed(() => {
  return confirmDeleteInput.value === namespaceToDelete.value?.meta.name;
});

onMounted(() => {
  store.dispatch('epinio/me');
  // Opens the create namespace modal if the query is passed as query param
  if (store.$router.currentRoute._value.query.mode === 'openModal') {
    openCreateModal();
  }

  startPolling(['namespaces', 'applications', 'configurations'], store);
});

onUnmounted(() => {
  stopPolling(['namespaces', 'applications', 'configurations']);
});

async function openCreateModal() {
  showCreateModal.value = true;
  // Create a skeleton namespace
  value.value = await store.dispatch(
    `epinio/create`,
    { type: EPINIO_TYPES.NAMESPACE },
  );
}

function closeCreateModal() {
  showCreateModal.value = false;
  errors.value = [];
}

async function onSubmitCreate() {
  creatingNamespace.value = true;
  try {
    await value.value.create();
    store.dispatch('epinio/refreshList', { type: EPINIO_TYPES.NAMESPACE });
    closeCreateModal();
  } catch (e) {
    errors.value = [];
    errors.value = epinioExceptionToErrorsArray(e).map(JSON.stringify);
  } finally {
    creatingNamespace.value = false;
  }
}

function getNamespaceErrors(name: string) {
  const kubernetesErrors = validateKubernetesName(
    name || '',
    t('epinio.namespace.name'),
    store.getters,
    undefined,
    [],
  );

  if (kubernetesErrors.length) {
    return [kubernetesErrors.join(', ')];
  }

  const validateName = name.match(/[a-z0-9]([-a-z0-9]*[a-z0-9])?/);

  if (
    !validateName ||
    validateName[0] !== name &&
    !errors.value.includes(t('epinio.namespace.validations.name'))
  ) {
    return [t('epinio.namespace.validations.name')];
  }

  return [];
}

function openDeleteModal() {
  showDeleteModal.value = true;
}

function closeDeleteModal() {
  showDeleteModal.value = false;
  confirmDeleteInput.value = '';
  errors.value = [];
}

async function onSubmitDelete() {
  if (!namespaceToDelete.value) {
    return;
  }
  try {
    deletingNamespace.value = true;
    await namespaceToDelete.value.remove();
    closeDeleteModal();
    // Was dispatched unprefixed ('findAll') against the root store, so it never
    // ran: apps and configs in the deleted namespace stayed in the store until
    // the next poll, keeping the per-namespace counts stale.
    store.dispatch('epinio/findAll', { type: EPINIO_TYPES.APP, opt: { force: true } });
    store.dispatch('epinio/refreshList', { type: EPINIO_TYPES.NAMESPACE });
    store.dispatch('epinio/refreshList', { type: EPINIO_TYPES.CONFIGURATION });
  } catch(e) {
    errors.value = [];
    errors.value = epinioExceptionToErrorsArray(e).map(JSON.stringify);
  } finally {
    deletingNamespace.value = false;
  }
}

const columns = [
  {
    field: 'meta.name',
    label: 'Name'
  },
  {
    field: 'appCount',
    label: 'Applications'
  },
  {
    field: 'configCount',
    label: 'Configurations'
  },
  {
    field: 'meta.createdAt',
    label: 'Age',
    formatter: 'age'
  }
];

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_unref(Masthead), {
      schema: _ctx.schema,
      resource: _unref(resource)
    }, {
      createButton: _withCtx(() => [
        (canCreateNamespace.value)
          ? (_openBlock(), _createElementBlock("trailhand-button", {
              key: 0,
              variant: "primary",
              size: "large",
              onClick: openCreateModal
            }, _toDisplayString(_unref(t)('generic.create')), 1))
          : (_openBlock(), _createElementBlock("div", _hoisted_2))
      ]),
      _: 1
    }, 8, ["schema", "resource"]),
    _cache[20] || (_cache[20] = _createTextVNode()),
    _createElementVNode("div", _hoisted_3, [
      _createElementVNode("trailhand-text-input", {
        value: searchQuery.value,
        placeholder: "Search...",
        onTextInputChange: _cache[0] || (_cache[0] = (e) => searchQuery.value = e.detail.value)
      }, null, 40, _hoisted_4)
    ]),
    _cache[21] || (_cache[21] = _createTextVNode()),
    _createElementVNode("trailhand-table", {
      ref: (el) => { if (el) el.renderActions = _unref(makeActionMenu); },
      rows: displayRows.value,
      columns: columns,
      "server-side": !!paginationMeta.value,
      "total-items": paginationMeta.value?.totalItems ?? displayRows.value.length,
      "current-page": currentPage.value,
      loading: paginating.value,
      searchable: false,
      "key-field": "_key",
      onPageChange: _cache[1] || (_cache[1] = (e) => goToPage(e.detail.page))
    }, null, 40, _hoisted_5),
    _cache[22] || (_cache[22] = _createTextVNode()),
    _createElementVNode("trailhand-modal", {
      ".open": showCreateModal.value,
      title: _unref(t)('epinio.namespace.create'),
      onModalOpen: _cache[4] || (_cache[4] = () => namespaceNameInput.value?.focus()),
      onModalClose: closeCreateModal
    }, [
      _createElementVNode("div", _hoisted_7, [
        _createElementVNode("trailhand-text-input", {
          ref_key: "namespaceNameInput",
          ref: namespaceNameInput,
          value: value.value.meta.name,
          placeholder: "Namespace Name",
          label: _unref(t)('epinio.namespace.name'),
          required: true,
          size: "large",
          onTextInputChange: _cache[2] || (_cache[2] = ($event: any) => (value.value.meta.name = $event.detail.value)),
          onKeydown: _cache[3] || (_cache[3] = (e) => { if (e.key === 'Enter' && validateCreate.value) onSubmitCreate(); })
        }, null, 40, _hoisted_8),
        _cache[8] || (_cache[8] = _createTextVNode()),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(errors.value, (err, i) => {
          return (_openBlock(), _createBlock(Banner, {
            key: i,
            color: "error",
            label: err
          }, null, 8, ["label"]))
        }), 128))
      ]),
      _cache[10] || (_cache[10] = _createTextVNode()),
      _createElementVNode("div", _hoisted_9, [
        _createElementVNode("trailhand-button", {
          variant: "secondary",
          class: "mr-10",
          onButtonClick: closeCreateModal
        }, "Cancel", 32),
        _cache[9] || (_cache[9] = _createTextVNode()),
        _createElementVNode("trailhand-button", {
          disabled: !validateCreate.value || creatingNamespace.value,
          variant: "primary",
          onButtonClick: onSubmitCreate
        }, _toDisplayString(creatingNamespace.value ? 'Creating...' : _unref(t)('generic.create')), 41, _hoisted_10)
      ])
    ], 40, _hoisted_6),
    _cache[23] || (_cache[23] = _createTextVNode()),
    _createElementVNode("trailhand-modal", {
      ".open": showDeleteModal.value,
      title: "Are you sure?",
      onModalOpen: _cache[7] || (_cache[7] = () => deleteNamespaceInput.value?.focus()),
      onModalClose: closeDeleteModal
    }, [
      _createElementVNode("div", _hoisted_12, [
        _createElementVNode("p", null, [
          _cache[11] || (_cache[11] = _createTextVNode("You are attempting to delete the Namespace ", -1)),
          _createElementVNode("strong", null, _toDisplayString(namespaceToDelete.value?.meta.name), 1),
          _cache[12] || (_cache[12] = _createTextVNode(".", -1))
        ]),
        _cache[15] || (_cache[15] = _createTextVNode()),
        _createElementVNode("p", null, [
          _cache[13] || (_cache[13] = _createTextVNode("Enter ", -1)),
          _createElementVNode("strong", null, _toDisplayString(namespaceToDelete.value?.meta.name), 1),
          _cache[14] || (_cache[14] = _createTextVNode(" below to confirm:", -1))
        ]),
        _cache[16] || (_cache[16] = _createTextVNode()),
        _createElementVNode("trailhand-text-input", {
          ref_key: "deleteNamespaceInput",
          ref: deleteNamespaceInput,
          value: confirmDeleteInput.value,
          size: "large",
          onTextInputChange: _cache[5] || (_cache[5] = ($event: any) => (confirmDeleteInput.value = $event.detail.value)),
          onKeydown: _cache[6] || (_cache[6] = (e) => { if (e.key === 'Enter' && validateDelete.value) onSubmitDelete(); })
        }, null, 40, _hoisted_13),
        _cache[17] || (_cache[17] = _createTextVNode()),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(errors.value, (err, i) => {
          return (_openBlock(), _createBlock(Banner, {
            key: i,
            color: "error",
            label: err
          }, null, 8, ["label"]))
        }), 128))
      ]),
      _cache[19] || (_cache[19] = _createTextVNode()),
      _createElementVNode("div", _hoisted_14, [
        _createElementVNode("trailhand-button", {
          variant: "secondary",
          class: "mr-10",
          onButtonClick: closeDeleteModal
        }, "Cancel", 32),
        _cache[18] || (_cache[18] = _createTextVNode()),
        _createElementVNode("trailhand-button", {
          disabled: !validateDelete.value || deletingNamespace.value,
          variant: "destructive",
          onButtonClick: onSubmitDelete
        }, _toDisplayString(deletingNamespace.value ? 'Deleting...' : _unref(t)('generic.delete')), 41, _hoisted_15)
      ])
    ], 40, _hoisted_11)
  ]))
}
}

})